import * as Select from '@radix-ui/react-select'
import { ChevronDownIcon } from 'lucide-react'

import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { SalesPointCategory } from '@/types/sales-network'

export type AreaSearchPanelProps = {
  countryTerms: SalesPointCategory[]
  regionTerms: SalesPointCategory[]
  selectedCountryId: number | null
  selectedRegionId: number | null
  onCountrySelect: (_id: number | null) => void
  onRegionSelect: (_id: number | null) => void
  onUseCurrentLocation: () => void
}

const TRIGGER_CLASS =
  'flex w-[100%] md:w-auto h-[4.8rem] min-w-[22rem] appearance-none items-center justify-between gap-spacing-xs rounded-radius-minimal border border-[rgba(255,255,255,.3)] bg-transparent px-[1.6rem] font-roboto text-d-small text-[white] outline-none focus:border-[rgba(255,255,255,.4)] focus:ring-2 focus:ring-[rgba(255,255,255,.2)]'

const CONTENT_CLASS =
  'z-50 mt-spacing-2xs max-h-[300px] w-[var(--radix-select-trigger-width)] overflow-auto rounded-radius-minimal border border-[rgba(255,255,255,.3)] bg-black-light'

const ITEM_CLASS =
  'cursor-pointer rounded-[0.5rem] px-[1.5rem] py-[1.2rem] font-roboto text-d-small text-[white] data-[highlighted]:bg-[white]/5 data-[state=checked]:bg-[white]/10'

const AreaSearchPanel = ({
  countryTerms,
  regionTerms,
  selectedCountryId,
  selectedRegionId,
  onCountrySelect,
  onRegionSelect
}: AreaSearchPanelProps) => {
  const { strings } = useStore()

  return (
    <div className="flex flex-wrap items-center gap-spacing-xs *:!text-[12.8px]">
      {/* Seleziona Stato */}

      <Select.Root
        value={selectedCountryId?.toString() ?? ''}
        onValueChange={(v) => onCountrySelect(v ? Number(v) : null)}
      >
        <Select.Trigger className={TRIGGER_CLASS}>
          <Select.Value
            placeholder={
              strings?.labels?.sales_network_country_select || 'Seleziona Stato'
            }
          />
          <Select.Icon>
            <ChevronDownIcon size={15} aria-hidden="true" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className={CONTENT_CLASS}
            data-lenis-prevent="true"
          >
            <Select.Viewport className="p-1">
              {countryTerms.map((term) => (
                <Select.Item
                  key={term.term_id}
                  value={term.term_id.toString()}
                  className={ITEM_CLASS}
                >
                  <Select.ItemText>{term.name}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {/* Seleziona Regione — sempre visibile, disabilitata se assente */}

      <Select.Root
        value={selectedRegionId?.toString() ?? ''}
        onValueChange={(v) => onRegionSelect(v ? Number(v) : null)}
        disabled={regionTerms.length === 0}
      >
        <Select.Trigger
          className={cn(TRIGGER_CLASS, {
            'cursor-not-allowed text-[white]/30': regionTerms.length === 0
          })}
        >
          <Select.Value
            placeholder={
              strings?.labels?.sales_network_region_select ||
              'Seleziona Regione'
            }
          />
          <Select.Icon>
            <ChevronDownIcon size={15} aria-hidden="true" />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          <Select.Content
            position="popper"
            className={CONTENT_CLASS}
            data-lenis-prevent="true"
          >
            <Select.Viewport className="p-1">
              {regionTerms.map((term) => (
                <Select.Item
                  key={term.term_id}
                  value={term.term_id.toString()}
                  className={ITEM_CLASS}
                >
                  <Select.ItemText>{term.name}</Select.ItemText>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {/* <div className="flex flex-1 justify-start whitespace-nowrap md:justify-end">
        <GeolocationCta onClick={onUseCurrentLocation} />
      </div> */}
    </div>
  )
}

export default AreaSearchPanel
